import * as React from 'react'
import classNames from 'classnames'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import { faTwitch } from '@fortawesome/free-brands-svg-icons'
import { faChevronDown, faChevronUp, faCircleInfo, faComputer, faDragon, faGlobe, faGripVertical, faHashtag, faICursor, faPlus, faUser } from '@fortawesome/free-solid-svg-icons'
import { ReactComponent as OBSLogo } from '../../assets/obs.svg'
import styles from './ActionPreview.module.scss'

export type PreviewKind =
  | 'twitch'
  | 'obs'
  | 'delay'
  | 'variable'
  | 'conditional'
  | 'actionset'
  | 'contextwindow'
  | 'command'
  | 'sound'
  | 'tts'
  | 'random'
  | 'playlist'
  | 'voice'
  | 'bot'
  | 'notification'

export const VariableToken: React.FC<React.PropsWithChildren<{ subtle?: boolean }>> = ({ children, subtle }) => (
  <span className={classNames(styles.variableToken, subtle && styles.subtleVariable)}>{children}</span>
)

/**
 * A compact line-item field used by actions whose editor pairs a variable/parameter
 * name with a value control.
 */
export const PreviewField: React.FC<
  React.PropsWithChildren<{
    label: string
    hint?: string
    wide?: boolean
    chevron?: boolean
  }>
> = ({ label, hint, wide, chevron = true, children }) => (
  <div className={classNames(styles.field, wide && styles.fieldWide)}>
    <div className={styles.fieldLabel}>
      {label}
      {hint ? <span className={styles.fieldHint}>{hint}</span> : null}
    </div>
    <div className={styles.fieldValue}>
      <span>{children}</span>
      {chevron ? <FontAwesomeIcon icon={faChevronDown as IconDefinition} /> : null}
    </div>
  </div>
)

/** Mirrors the self-contained TextField/Combobox style used throughout the app. */
export const PreviewInput: React.FC<
  React.PropsWithChildren<{
    label: string
    chevron?: boolean
    icon?: IconDefinition
    iconElement?: React.ReactNode
  }>
> = ({ label, chevron = false, icon, iconElement, children }) => (
  <div className={styles.previewInput}>
    <div className={styles.previewInputLabel}>{label}</div>
    <div className={styles.previewInputValue}>
      <span className={styles.previewInputText}>
        {iconElement}
        {icon ? <FontAwesomeIcon icon={icon} /> : null}
        {children}
      </span>
      {chevron ? <FontAwesomeIcon icon={faChevronDown as IconDefinition} className={styles.previewInputChevron} /> : null}
    </div>
  </div>
)

export const PreviewInputRow: React.FC<React.PropsWithChildren> = ({ children }) => <div className={styles.previewInputRow}>{children}</div>

export const PreviewMessageField: React.FC<React.PropsWithChildren> = ({ children }) => (
  <div className={styles.messageField}>
    <div className={styles.variablePicker}>
      <FontAwesomeIcon icon={faICursor as IconDefinition} />
      <span>Insert Variable into Chat Message</span>
      <FontAwesomeIcon icon={faChevronDown as IconDefinition} />
    </div>
    <div className={styles.messageBox}>
      <div className={styles.messageLabel}>Message Text</div>
      <div className={styles.messageText}>{children}</div>
    </div>
  </div>
)

const EditorToolbar: React.FC = () => (
  <div className={styles.editorToolbar} aria-hidden='true'>
    <span>
      <FontAwesomeIcon icon={faDragon as IconDefinition} /> Draco.Bot <FontAwesomeIcon icon={faChevronDown as IconDefinition} />
    </span>
    <span>
      <FontAwesomeIcon icon={faTwitch as IconDefinition} /> Twitch <FontAwesomeIcon icon={faChevronDown as IconDefinition} />
    </span>
    <span>
      <FontAwesomeIcon icon={faComputer as IconDefinition} /> Integrations <FontAwesomeIcon icon={faChevronDown as IconDefinition} />
    </span>
    <span>
      <FontAwesomeIcon icon={faGlobe as IconDefinition} /> Utilities <FontAwesomeIcon icon={faChevronDown as IconDefinition} />
    </span>
  </div>
)

export const NestedActions: React.FC<React.PropsWithChildren<{ caption: string; empty?: boolean; toolbar?: boolean }>> = ({ caption, children, empty, toolbar }) => (
  <div className={styles.nestedActions}>
    <div className={styles.nestedCaption}>{caption}</div>
    {toolbar ? <EditorToolbar /> : null}
    <div className={classNames(styles.nestedContent, empty && styles.emptyNested)}>
      {empty ? (
        <div className={styles.emptyMessage}>
          <FontAwesomeIcon icon={faPlus as IconDefinition} />
          No Actions Added
        </div>
      ) : (
        children
      )}
    </div>
  </div>
)

export const ActionStack: React.FC<React.PropsWithChildren<{ compact?: boolean }>> = ({ compact, children }) => (
  <div className={classNames(styles.actionStack, compact && styles.compactStack)}>{children}</div>
)

type ActionSetVariable = {
  name: string
  type: 'Username' | 'Text' | 'Number' | 'Scene' | 'Source'
}

const variableIcon = (type: ActionSetVariable['type']): { icon?: IconDefinition; iconElement?: React.ReactNode } => {
  if (type === 'Username') return { icon: faUser as IconDefinition }
  if (type === 'Number') return { icon: faHashtag as IconDefinition }
  if (type === 'Scene' || type === 'Source') {
    return { iconElement: <OBSLogo className={styles.inlineObsIcon} /> }
  }
  return { icon: faICursor as IconDefinition }
}

/**
 * Read-only Learn-page facsimile of the Action Set definition editor. It deliberately
 * contains no runtime/editor state; it only reproduces the visual hierarchy users see.
 */
export const ActionSetEditorPreview: React.FC<
  React.PropsWithChildren<{
    name: string
    variables: ActionSetVariable[]
  }>
> = ({ name, variables, children }) => (
  <div className={styles.actionSetEditor}>
    <div className={styles.editorTitle}>Action Set Editor</div>
    <PreviewInput label='Action Set Name *'>{name}</PreviewInput>

    <div className={styles.actionSetVariablesHeader}>
      <strong>Action Set Variables</strong>
      <span className={styles.fakeAddButton}>
        <FontAwesomeIcon icon={faPlus as IconDefinition} /> Add Variable
      </span>
    </div>

    <div className={styles.actionSetVariables}>
      {variables.map((variable) => (
        <PreviewInputRow key={variable.name}>
          <PreviewInput label='Variable Name *'>{variable.name}</PreviewInput>
          <PreviewInput label='Variable Type' chevron {...variableIcon(variable.type)}>
            {variable.type}
          </PreviewInput>
        </PreviewInputRow>
      ))}
    </div>

    <div className={styles.actionSetActions}>
      <EditorToolbar />
      <div className={styles.actionSetActionsContent}>{children}</div>
    </div>
  </div>
)

type ConditionalRulePreviewProps = {
  left: React.ReactNode
  compare: React.ReactNode
  right: React.ReactNode
  join?: 'When' | 'And' | 'Or'
}

const ConditionalRulePreview: React.FC<ConditionalRulePreviewProps> = ({ left, compare, right, join = 'When' }) => (
  <div className={styles.conditionalRule}>
    <span className={classNames(styles.conditionBadge, join !== 'When' && styles.secondaryConditionBadge)}>{join}</span>
    <div className={styles.conditionValue}>
      {left}
      <FontAwesomeIcon icon={faChevronDown as IconDefinition} />
    </div>
    <div className={styles.conditionValue}>
      {compare}
      <FontAwesomeIcon icon={faChevronDown as IconDefinition} />
    </div>
    <div className={styles.conditionValue}>
      {right}
      <FontAwesomeIcon icon={faChevronDown as IconDefinition} />
    </div>
    <span className={styles.conditionAdd}>
      <FontAwesomeIcon icon={faPlus as IconDefinition} />
    </span>
  </div>
)

/** Read-only facsimile of the Conditional Actions editor used in Draco.Bot. */
export const ConditionalEditorPreview: React.FC<{
  rules: ConditionalRulePreviewProps[]
  matchMode?: 'all' | 'any'
  whenMatched?: React.ReactNode
  whenNotMatched?: React.ReactNode
  blockOnMatch?: boolean
  blockOnFail?: boolean
}> = ({ rules, matchMode = 'all', whenMatched, whenNotMatched, blockOnMatch = false, blockOnFail = false }) => (
  <div className={styles.conditionalEditor}>
    <div className={styles.conditionalTitleRow}>
      <span>Conditions match when</span>
      <div className={styles.conditionMode}>
        {matchMode === 'all' ? 'All of the following are true' : 'Any of the following is true'}
        <FontAwesomeIcon icon={faChevronDown as IconDefinition} />
      </div>
    </div>

    <div className={styles.conditionalRules}>
      {rules.map((rule, index) => (
        <ConditionalRulePreview key={index} {...rule} join={index === 0 ? 'When' : matchMode === 'all' ? 'And' : 'Or'} />
      ))}
    </div>

    <NestedActions caption={matchMode === 'all' ? 'When all conditions match' : 'When any condition matches'} toolbar empty={!whenMatched}>
      {whenMatched}
    </NestedActions>
    <NestedActions caption={matchMode === 'all' ? 'When one or more conditions do not match' : 'When no condition matches'} toolbar empty={!whenNotMatched}>
      {whenNotMatched}
    </NestedActions>

    <div className={styles.conditionalFooter}>
      <div className={styles.toggleLine}>
        <span className={classNames(styles.fakeToggle, blockOnMatch && styles.fakeToggleOn)}>
          <span />
        </span>
        <span>If conditions match, stop after this conditional action.</span>
      </div>
      <div className={styles.toggleLine}>
        <span className={classNames(styles.fakeToggle, blockOnFail && styles.fakeToggleOn)}>
          <span />
        </span>
        <span>If conditions do not match, stop after this conditional action.</span>
      </div>
    </div>
  </div>
)

type QueueOptionPreview = {
  title: string
  description: string
  icon: IconDefinition
  selected?: boolean
}

/** Read-only facsimile of the Queueing Behavior dropdown shown by command/response editors. */
export const QueueOptionsPreview: React.FC<{ options: QueueOptionPreview[] }> = ({ options }) => {
  const selected = options.find((option) => option.selected) ?? options[0]

  return (
    <div className={styles.appPreviewTheme}>
      <div className={styles.queuePreview}>
        <div className={styles.queueMenu}>
          {options.map((option) => (
            <div key={option.title} className={classNames(styles.queueOption, option.selected && styles.queueOptionSelected)}>
              <FontAwesomeIcon icon={option.icon} />
              <div>
                <strong>{option.title}</strong>
                <span>{option.description}</span>
              </div>
            </div>
          ))}
        </div>
        <div className={styles.queueSelectedValue}>
          <FontAwesomeIcon icon={selected.icon} />
          <span>{selected.title}</span>
          <FontAwesomeIcon icon={faChevronUp as IconDefinition} />
        </div>
      </div>
    </div>
  )
}

type ActionPreviewProps = React.PropsWithChildren<{
  kind: PreviewKind
  title: string
  subtitle?: string
  icon?: IconDefinition
  details?: React.ReactNode
  note?: React.ReactNode
  vertical?: boolean
  information?: string
}>

const ActionPreview: React.FC<ActionPreviewProps> = ({ kind, title, subtitle, icon, details, note, vertical = true, information, children }) => {
  return (
    <div className={styles.appPreviewTheme}>
      <div className={classNames(styles.action, styles[kind], vertical && styles.vertical)}>
        <div className={styles.actionHeader}>
          <div className={styles.dragHandle} aria-hidden='true'>
            <FontAwesomeIcon icon={faGripVertical as IconDefinition} />
          </div>
          <div className={styles.actionIcon}>
            {kind === 'obs' ? (
              <OBSLogo className={styles.obsActionIcon} />
            ) : kind === 'variable' ? (
              <span className={styles.variableActionIcon}>{'{}'}</span>
            ) : icon ? (
              <FontAwesomeIcon icon={icon} />
            ) : null}
          </div>
          <div className={styles.actionName}>
            <span className={styles.actionTitle}>{title}</span>
            {subtitle ? (
              <>
                <span className={styles.actionDash}> - </span>
                <span className={styles.actionSubtitle}>{subtitle}</span>
              </>
            ) : null}
          </div>
          {information ? (
            <span className={styles.infoIcon} title={information} aria-label={information}>
              <FontAwesomeIcon icon={faCircleInfo as IconDefinition} />
            </span>
          ) : null}
        </div>
        {details || children ? (
          <div className={styles.actionDetails}>
            {details ? <div className={styles.detailsText}>{details}</div> : null}
            {children}
            {note ? <div className={styles.actionNote}>{note}</div> : null}
          </div>
        ) : null}
      </div>
    </div>
  )
}

export default ActionPreview
